/**
 * BuildJobsContext — 全域追蹤 ongoing build / chat SSE streams
 *
 * 為什麼放 App 層：路由切換 + wizard 關閉都不 unmount，stream 不會斷
 */
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { auth } from '../../auth/firebase.js';
import { onAuthStateChanged } from 'firebase/auth';
import { storedHostId } from '../TenantContext.jsx';

const BuildJobsContext = createContext(null);

/**
 * computeBuildProgress(events) → { percent: 0-100, stageKey: string, stageParams: object, stage: string }
 *
 * `stageKey` 是 i18n key（ivrAgents namespace），`stageParams` 是插值參數；
 * 為向下相容仍同時回 `stage`（= stageKey，由 consumer 用 t(progress.stage, progress.stageParams) 渲染）。
 *
 * New hybrid pipeline (build_kb_hybrid.js orchestrator) stages:
 *   create_avatar       10%
 *   build_kb_hybrid     15-65%  (ramps with per-file completion from stderr log lines)
 *   write_kb            75%
 *   vectorize           85%
 *   build_flow          92%
 *   generate-greeting   95%
 *   finalize            98%
 *   result              100%
 *
 * The build_kb_hybrid step emits stderr lines we tail to interpolate progress
 * within its window:
 *   "[orchestrator] starting N files in parallel"  → captures total files
 *   "[<filename>] ✅ wrote ..."                    → +1 file done
 */
export function computeBuildProgress(events) {
    const out = (percent, stageKey, stageParams = {}) => ({ percent, stageKey, stageParams, stage: stageKey });

    if (!events || events.length === 0) return out(0, 'build.stage.preparing');

    // 終止狀態
    const finalEv = [...events].reverse().find(e => e.type === 'result' || e.type === 'completed');
    if (finalEv) {
        if (finalEv.status === 'failed') return out(100, 'build.stage.failed');
        return out(100, 'build.stage.done');
    }

    let percent = 5;
    let stageKey = 'build.stage.initializing';
    let stageParams = {};
    let currentTool = null;
    let currentInput = '';
    let totalFiles = 0;
    let filesDone = 0;
    let webFetchCount = 0;   // URL builds: 每個 WebFetch tool_result +1

    const advance = (p, k, params = {}) => {
        if (p > percent) { percent = p; stageKey = k; stageParams = params; }
    };

    for (const ev of events) {
        const t = ev.type;

        // Stderr log lines from chunking orchestrator come through as type=log
        if (t === 'log' && typeof ev.message === 'string') {
            const m = ev.message;
            const matchTotal = m.match(/starting (\d+) files? in parallel/);
            if (matchTotal) totalFiles = Math.max(totalFiles, Number(matchTotal[1]));
            if (/✅ wrote/.test(m) && /^\[[^\]]+\]/.test(m)) filesDone += 1;   // per-file completion
            if (totalFiles > 0) {
                // Map filesDone/totalFiles into window 15-65%
                const within = 15 + (50 * filesDone / totalFiles);
                if (filesDone < totalFiles) advance(within, 'build.stage.processingFiles', { done: filesDone, total: totalFiles });
                else advance(within, 'build.stage.collectingKnowledge');
            }
            continue;
        }

        if (t === 'tool_start') {
            currentTool = ev.name;
            currentInput = '';
        } else if (t === 'tool_input_delta' && currentTool) {
            currentInput += ev.json || '';
        } else if (t === 'tool_result' || t === 'tool_end') {
            const cmd = currentInput;
            if (currentTool === 'Bash') {
                if (/create_avatar\.js/.test(cmd))            advance(10, 'build.stage.creatingAgent');
                else if (/build_kb_hybrid\.js/.test(cmd))     advance(65, 'build.stage.collectingKnowledge');
                else if (/write_kb\.js/.test(cmd))            advance(75, 'build.stage.buildingKb');
                else if (/vectorize\.js/.test(cmd))           advance(85, 'build.stage.vectorizing');
                else if (/build_flow\.js/.test(cmd))          advance(92, 'build.stage.generatingFlow');
                else if (/generate-greeting/.test(cmd))       advance(95, 'build.stage.generatingGreeting');
                else if (/finalize\.js/.test(cmd))            advance(98, 'build.stage.finalizing');
            } else if (currentTool === 'WebFetch') {
                // URL build: 每抓完一個 URL 就在 10-15% 區間往前一點
                webFetchCount += 1;
                advance(Math.min(15, 10 + webFetchCount), 'build.stage.fetchingWeb', { n: webFetchCount });
            }
            currentTool = null;
            currentInput = '';
        }
    }

    // Pending tool — currentTool != null means we're mid-way (tool_start fired but tool_result hasn't).
    // For long-running Bash commands (build_kb_hybrid takes 30-60s with no intermediate events),
    // ramp progress by elapsed wall-clock time since the latest tool_start event.
    if (currentTool === 'Bash') {
        // Find timestamp of the latest tool_start in events
        let lastStartAt = null;
        for (let i = events.length - 1; i >= 0; i--) {
            if (events[i].type === 'tool_start') { lastStartAt = events[i]._recvAt || Date.now(); break; }
        }
        if (lastStartAt) {
            const elapsed = (Date.now() - lastStartAt) / 1000;
            const cmd = currentInput;
            if (/build_kb_hybrid\.js/.test(cmd)) {
                // 15% baseline, +1% per second, capped at 60% — leaves headroom for tool_result to jump to 65%
                advance(Math.min(60, Math.round(15 + elapsed)), 'build.stage.processingDocs', { elapsed: Math.round(elapsed) });
            } else if (/vectorize\.js/.test(cmd)) {
                advance(Math.min(83, Math.round(75 + elapsed * 0.5)), 'build.stage.vectorizingElapsed', { elapsed: Math.round(elapsed) });
            } else if (/generate-greeting/.test(cmd)) {
                advance(Math.min(94, Math.round(92 + elapsed * 0.3)), 'build.stage.generatingGreetingElapsed', { elapsed: Math.round(elapsed) });
            }
        }
    }

    return out(percent, stageKey, stageParams);
}

export function BuildJobsProvider({ children }) {
    const [jobs, setJobs] = useState({}); // { [jobId]: { type, name, avatarId, sessionId, status, events, controller, startedAt } }

    const start = useCallback((spec) => {
        const jobId = spec.jobId || `job-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setJobs((prev) => ({
            ...prev,
            [jobId]: {
                jobId,
                type: spec.type || 'chat',          // 'chat' | 'build'
                name: spec.name || 'Untitled',
                avatarId: spec.avatarId || null,
                sessionId: spec.sessionId || null,
                status: 'running',
                events: [],
                controller: spec.controller || null,
                startedAt: Date.now(),
            },
        }));
        return jobId;
    }, []);

    const appendEvent = useCallback((jobId, event) => {
        setJobs((prev) => {
            const j = prev[jobId];
            if (!j) return prev;
            return { ...prev, [jobId]: { ...j, events: [...j.events, event] } };
        });
    }, []);

    const finish = useCallback((jobId, finalStatus = 'completed', extra = {}) => {
        setJobs((prev) => {
            const j = prev[jobId];
            if (!j) return prev;
            return { ...prev, [jobId]: { ...j, status: finalStatus, ...extra } };
        });
    }, []);

    const abort = useCallback((jobId) => {
        setJobs((prev) => {
            const j = prev[jobId];
            if (!j) return prev;
            try { j.controller?.abort(); } catch {}
            return { ...prev, [jobId]: { ...j, status: 'interrupted' } };
        });
    }, []);

    const remove = useCallback((jobId) => {
        setJobs((prev) => {
            const { [jobId]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const getByAvatar = useCallback((avatarId) => {
        return Object.values(jobs).find((j) => j.avatarId === avatarId && j.status === 'running');
    }, [jobs]);

    /**
     * startAvatarBuild — App 級 build job：fetch + SSE parsing 都在 context
     * 即使 wizard 關閉 modal 卸載，build 仍繼續在背景跑
     *
     * ⚠️ 關鍵：build 跑 5-10 分鐘，中間「爬整站 / 切 chunks / vectorize」會有數分鐘
     * 無事件的靜默階段，連線可能被中間層（ALB idle / NAT / 瀏覽器背景分頁節流）掐斷。
     * 後端偵測到斷線後有 15min grace 會「繼續把 build 跑完」，所以**連線斷 ≠ build 失敗**。
     * 這裡在 SSE 中途斷掉時，只要已知 sessionId 就自動重連 /build/:sid/stream（backoff），
     * 把 server 端 buffer 的事件補回，只有真的拿不到 session / server 回報 failed 才標失敗。
     */
    const startAvatarBuild = useCallback(async (spec) => {
        const jobId = `build-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const controller = new AbortController();
        setJobs((prev) => ({
            ...prev,
            [jobId]: {
                jobId,
                type: 'build',
                name: spec.name || 'New Avatar',
                mode: spec.mode,
                hostId: spec.hostId || null,   // 列表頁依當下 tenant 過濾（切 tenant 後不該還看到）
                avatarId: null,
                sessionId: null,
                status: 'running',
                events: [],
                controller,
                startedAt: Date.now(),
            },
        }));

        // sessionId 從第一個 `started` event 拿到後存這（重連用）；reconnect 後也共用
        let sessionId = null;

        // 把一個 SSE event 套進 job 狀態。回傳 true 代表這是 terminal event（build 真的結束）。
        const applyEvent = (ev) => {
            let terminal = false;
            if (ev.type === 'started' && ev.sessionId) sessionId = ev.sessionId;
            if ((ev.type === 'result' || ev.type === 'completed') && ev.avatarId) terminal = true;
            if (ev.type === 'error' || ev.type === 'failed' || ev.type === 'replay-complete') terminal = true;
            setJobs((prev) => {
                const j = prev[jobId];
                if (!j) return prev;
                const patch = { events: [...j.events, ev] };
                if (ev.type === 'started' && ev.sessionId) patch.sessionId = ev.sessionId;
                // backend 完成 event：{ type:'result', status, avatarId } / { type:'completed', avatarId }
                if ((ev.type === 'result' || ev.type === 'completed') && ev.avatarId) {
                    patch.avatarId = ev.avatarId;
                    if (ev.status === 'failed') {
                        patch.status = 'failed';
                        patch.error = ev.error || ev.message;
                    } else {
                        patch.status = 'completed';
                        window.dispatchEvent(new CustomEvent('qubby:avatar-changed', { detail: { reason: 'created', avatarId: ev.avatarId } }));
                    }
                }
                if (ev.type === 'error' || ev.type === 'failed') {
                    patch.status = 'failed';
                    patch.error = ev.message || ev.error;
                }
                if (ev.type === 'replay-complete') {
                    patch.status = ev.status === 'failed' ? 'failed' : 'completed';
                }
                return { ...prev, [jobId]: { ...j, ...patch } };
            });
            return terminal;
        };

        // 讀一條 SSE stream（POST 或 reconnect 共用）。
        // cancelOnTerminal=true：reconnect 的 /stream 在 terminal 後仍 live-tail 不會自動關，
        // 收到 terminal 就主動 cancel reader 收工；POST 串流會自然 done，不需 cancel。
        // 回傳：'terminal' | 'eof'（stream 正常結束但沒 terminal）
        const pump = async (reader, cancelOnTerminal) => {
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done) return 'eof';
                buf += decoder.decode(value, { stream: true });
                let nl;
                while ((nl = buf.indexOf('\n\n')) >= 0) {
                    const chunk = buf.slice(0, nl).trim();
                    buf = buf.slice(nl + 2);
                    if (!chunk.startsWith('data:')) continue;   // heartbeat（: hb）等 comment 忽略
                    try {
                        const ev = JSON.parse(chunk.slice(5).trim());
                        ev._recvAt = Date.now();
                        const terminal = applyEvent(ev);
                        if (terminal && cancelOnTerminal) {
                            try { await reader.cancel(); } catch {}
                            return 'terminal';
                        }
                        if (terminal) return 'terminal';
                    } catch {}
                }
            }
        };

        // 中途斷線 → 重連 /build/:sid/stream，backoff 重試。回傳是否成功收到 terminal。
        const reconnect = async () => {
            if (!sessionId) return false;
            const MAX = 40;
            for (let attempt = 0; attempt < MAX; attempt++) {
                if (controller.signal.aborted) return true;   // 使用者主動取消
                const delay = attempt === 0 ? 800 : Math.min(1500 * Math.pow(1.4, attempt), 15000);
                await new Promise((r) => setTimeout(r, delay));
                if (controller.signal.aborted) return true;
                try {
                    const user = auth.currentUser;
                    if (!user) continue;
                    const idToken = await user.getIdToken();
                    const res = await fetch(`/ivr-builder/api/ivr/build/${encodeURIComponent(sessionId)}/stream`, {
                        headers: { Authorization: `Bearer ${idToken}` },
                        signal: controller.signal,
                    });
                    if (res.status === 404) return false;   // session 不在記憶體（容器重啟）→ 無法重連
                    if (!res.ok) continue;
                    const outcome = await pump(res.body.getReader(), true);
                    if (outcome === 'terminal') return true;
                    // 'eof' 但沒 terminal（連線又被切）→ 再重連
                } catch (e) {
                    if (e.name === 'AbortError') return true;
                    // 其他（網路再斷）→ 繼續重試
                }
            }
            return false;
        };

        // 收尾：只在 job 仍 running 時才覆寫狀態，避免蓋掉已完成/失敗
        const finalizeRunning = (status, error) => {
            setJobs((prev) => {
                const j = prev[jobId];
                if (!j || j.status !== 'running') return prev;
                return { ...prev, [jobId]: { ...j, status, ...(error ? { error } : {}) } };
            });
        };

        // 背景 fetch（不 await — caller 用 jobs[jobId] 訂閱事件）
        (async () => {
            let outcome = 'eof';
            try {
                const user = auth.currentUser;
                if (!user) throw new Error('not authenticated');
                const idToken = await user.getIdToken();
                const res = await fetch('/ivr-builder/api/ivr/build', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${idToken}` },
                    body: spec.formData,
                    signal: controller.signal,
                });
                if (!res.ok) {
                    const txt = await res.text();
                    // 後端擋下（如角色數超過授權上限 409）會回 { error, message }；
                    // message 是人話，直接當畫面文案，不要吐 `HTTP 409: {"error":"role_limit_exceeded"…}`
                    let human = '';
                    try { human = (JSON.parse(txt)?.message || '').trim(); } catch { /* 非 JSON */ }
                    throw new Error(human || `HTTP ${res.status}: ${txt.substring(0, 200)}`);
                }
                outcome = await pump(res.body.getReader(), false);
            } catch (e) {
                if (e.name === 'AbortError') {
                    finalizeRunning('interrupted');
                    return;
                }
                // 連線中途斷 — 不要直接判失敗，先嘗試用 sessionId 重連把 build 接回來
                console.warn('[buildJobs] build stream dropped, will try reconnect:', e.message);
                if (sessionId) {
                    const ok = await reconnect();
                    if (!ok) finalizeRunning('failed', '連線中斷且無法重新連上建構工作');
                } else {
                    // 連 sessionId 都還沒拿到（POST 本身就沒成功）→ 真失敗
                    console.error('[buildJobs] build fail before session started:', e);
                    finalizeRunning('failed', e.message);
                }
                return;
            }

            // POST stream 正常結束
            if (outcome === 'terminal') return;   // 已收到 result/completed/failed，狀態已定
            // stream done 但沒 terminal event（連線被半關）→ 有 sessionId 就重連確認結果
            if (sessionId) {
                const ok = await reconnect();
                if (!ok) finalizeRunning('failed', '連線中斷且無法重新連上建構工作');
            } else {
                finalizeRunning('completed');   // 無 session 可查，沿用舊行為視為完成
            }
        })();

        return jobId;
    }, []);

    /**
     * resumeBuildSession — 用既有 sessionId 重新 attach SSE，把 server side buffered events 補回
     * 用於頁面 refresh 後拉回正在跑的 build。
     */
    const resumeBuildSession = useCallback(async (sessionInfo) => {
        const { sessionId, name, mode, avatarId, status: initialStatus, hostId: jobHostId } = sessionInfo;
        const jobId = `resume-${sessionId}`;
        // 已有同 sessionId 的 job 就不重複建（避免 hot reload 衝突）
        let alreadyTracked = false;
        setJobs((prev) => {
            if (Object.values(prev).some((j) => j.sessionId === sessionId)) {
                alreadyTracked = true;
                return prev;
            }
            return {
                ...prev,
                [jobId]: {
                    jobId,
                    type: 'build',
                    name: name || sessionId,
                    mode: mode || 'full',
                    avatarId: avatarId || null,
                    sessionId,
                    status: initialStatus || 'running',
                    events: [],
                    controller: null,
                    startedAt: Date.now(),
                    resumed: true,
                    hostId: jobHostId || null,   // 列表頁再依當下 tenant 過濾一次
                },
            };
        });
        if (alreadyTracked) return;

        (async () => {
            try {
                const user = auth.currentUser;
                if (!user) return;
                const idToken = await user.getIdToken();
                const res = await fetch(`/ivr-builder/api/ivr/build/${encodeURIComponent(sessionId)}/stream`, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                if (!res.ok) return;
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buf = '';
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });
                    let nl;
                    while ((nl = buf.indexOf('\n\n')) >= 0) {
                        const chunk = buf.slice(0, nl).trim();
                        buf = buf.slice(nl + 2);
                        if (!chunk.startsWith('data:')) continue;
                        try {
                            const ev = JSON.parse(chunk.slice(5).trim());
                            setJobs((prev) => {
                                const j = prev[jobId];
                                if (!j) return prev;
                                const patch = { events: [...j.events, ev] };
                                if ((ev.type === 'result' || ev.type === 'completed') && ev.avatarId) {
                                    patch.avatarId = ev.avatarId;
                                    if (ev.status === 'failed') {
                                        patch.status = 'failed';
                                        patch.error = ev.error || ev.message;
                                    } else {
                                        patch.status = 'completed';
                                        window.dispatchEvent(new CustomEvent('qubby:avatar-changed', { detail: { reason: 'created', avatarId: ev.avatarId } }));
                                    }
                                }
                                if (ev.type === 'error' || ev.type === 'failed') {
                                    patch.status = 'failed';
                                    patch.error = ev.message || ev.error;
                                }
                                if (ev.type === 'replay-complete') {
                                    patch.status = ev.status === 'failed' ? 'failed' : 'completed';
                                }
                                return { ...prev, [jobId]: { ...j, ...patch } };
                            });
                        } catch {}
                    }
                }
            } catch (e) {
                console.warn('[buildJobs] resume failed for', sessionId, e.message);
            }
        })();
    }, []);

    // On mount (and after auth ready): fetch active build sessions + re-attach SSE
    useEffect(() => {
        let cancelled = false;
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user || cancelled) return;
            try {
                const idToken = await user.getIdToken();
                // 帶「目前所在 tenant」給 server 做租戶邊界過濾（BuildJobsProvider 掛在 TenantProvider
                // 之上拿不到 context，直接讀 TenantContext 存的那把 localStorage key）
                const myHostId = storedHostId(user.uid);
                const qs = myHostId ? `?hostId=${encodeURIComponent(myHostId)}` : '';
                const r = await fetch(`/ivr-builder/api/ivr/build/sessions/active${qs}`, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                if (!r.ok) return;
                const data = await r.json();
                if (cancelled) return;
                const myEmail = (user.email || '').toLowerCase();
                for (const s of (data.sessions || [])) {
                    // 🔒 第二道防線（2026-08-10 東訊看到 aa-test01 的「qq」建構卡）：
                    // 就算 server 因為 proxy 沒帶 email 之類的原因漏放，前端也只認
                    // 「自己建的 + 在自己現在這個 tenant」的 build，其餘一律不畫卡片。
                    if (s.ownerEmail && myEmail && s.ownerEmail.toLowerCase() !== myEmail) continue;
                    if (s.hostId && myHostId && s.hostId !== myHostId) continue;
                    resumeBuildSession(s);
                }
            } catch (e) {
                console.warn('[buildJobs] fetch active sessions failed:', e.message);
            }
        });
        return () => { cancelled = true; unsub(); };
    }, [resumeBuildSession]);

    return (
        <BuildJobsContext.Provider value={{ jobs, start, appendEvent, finish, abort, remove, getByAvatar, startAvatarBuild, resumeBuildSession }}>
            {children}
        </BuildJobsContext.Provider>
    );
}

export function useBuildJobs() {
    const ctx = useContext(BuildJobsContext);
    if (!ctx) throw new Error('useBuildJobs must be inside BuildJobsProvider');
    return ctx;
}
